package at.theatervogelweide.admin.web.screens.club

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import at.theatervogelweide.admin.contract.AdminError
import at.theatervogelweide.admin.contract.AdminResult
import at.theatervogelweide.admin.contract.ErrorCode
import at.theatervogelweide.admin.contract.FieldError
import at.theatervogelweide.admin.contract.club.Club
import at.theatervogelweide.admin.contract.club.ClubDraft
import at.theatervogelweide.admin.contract.club.ClubService
import at.theatervogelweide.admin.web.Route
import at.theatervogelweide.admin.web.components.AdminButton
import at.theatervogelweide.admin.web.components.AdminCallout
import at.theatervogelweide.admin.web.components.AdminCard
import at.theatervogelweide.admin.web.components.AdminConflictCallout
import at.theatervogelweide.admin.web.components.AdminMarkdownField
import at.theatervogelweide.admin.web.components.AdminMarkdownView
import at.theatervogelweide.admin.web.components.AdminPageBody
import at.theatervogelweide.admin.web.components.AdminPageHeader
import at.theatervogelweide.admin.web.components.AdminTextAreaField
import at.theatervogelweide.admin.web.components.AdminTextView
import at.theatervogelweide.admin.web.components.Bem
import at.theatervogelweide.admin.web.components.ButtonVariant
import at.theatervogelweide.admin.web.components.ReportUnsavedChanges
import at.theatervogelweide.admin.web.components.RouteLink
import at.theatervogelweide.admin.web.components.UnsavedChangesGuard
import at.theatervogelweide.admin.web.rpcCall
import at.theatervogelweide.admin.web.screens.acts.Strong
import at.theatervogelweide.admin.web.screens.acts.formatDate
import kotlinx.coroutines.launch
import kotlinx.rpc.RpcClient
import kotlinx.rpc.withService
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private val alert = Bem("admin-alert")
private val button = Bem("admin-button")
private val card = Bem("admin-card")
private val fieldset = Bem("admin-fieldset")
private val stack = Bem("admin-stack")

/** The club's texts as loaded, and the texts being edited. */
private class ClubEditorState(val loaded: Club) {
    var draft: ClubDraft by mutableStateOf(loaded.draft)
    var fieldErrors: List<FieldError> by mutableStateOf(emptyList())
    var error: String? by mutableStateOf(null)
    var conflict: Boolean by mutableStateOf(false)
    var saving: Boolean by mutableStateOf(false)
    var reloading: Boolean by mutableStateOf(false)

    val busy: Boolean get() = saving || reloading

    val hasChanges: Boolean get() = draft != loaded.draft

    fun errorOf(field: String): String? = fieldErrors.firstOrNull { it.field == field }?.message

    /** Changes the draft; the field's error goes once it is edited. */
    fun edit(field: String, change: ClubDraft.() -> ClubDraft) {
        draft = draft.change()
        fieldErrors = fieldErrors.filterNot { it.field == field }
    }

    fun showFailure(failure: AdminError) {
        conflict = failure.code == ErrorCode.CONFLICT
        fieldErrors = failure.fieldErrors
        error = when {
            conflict -> null
            failure.fieldErrors.isNotEmpty() -> null
            else -> failure.message
        }
    }
}

/**
 * Verein (#106): the club's texts on the public site's Verein page and the start page. Superusers ([editable]) change
 * and save them; every other admin sees them read-only, and the server refuses their saves anyway. The contact e-mail
 * is the Impressum's (#147), which [onNavigate] leads to.
 */
@Composable
fun ClubScreen(rpc: RpcClient, guard: UnsavedChangesGuard, editable: Boolean, onNavigate: (Route) -> Unit) {
    val service = remember(rpc) { rpc.withService<ClubService>() }
    val scope = rememberCoroutineScope()

    var editor by remember { mutableStateOf<ClubEditorState?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }

    /** Loads the texts in place of [replacing]; the answer is dropped if the editor changed meanwhile. */
    suspend fun load(replacing: ClubEditorState? = editor) {
        val result = rpcCall { service.load() }
        if (editor !== replacing) {
            return
        }
        when (result) {
            is AdminResult.Success -> {
                editor = ClubEditorState(result.value)
                loadError = null
            }

            // a failed reload keeps the editor and says why in it
            is AdminResult.Failure -> replacing?.let { it.error = result.error.message } ?: run { loadError = result.error.message }
        }
    }

    LaunchedEffect(service) { load() }

    val state = editor
    ReportUnsavedChanges(guard, editable && state?.hasChanges == true)

    fun save() {
        val current = editor ?: return
        if (current.busy || !current.hasChanges) {
            return
        }
        current.conflict = false
        current.error = null
        current.fieldErrors = current.draft.validate()
        if (current.fieldErrors.isNotEmpty()) {
            return
        }
        current.saving = true
        scope.launch {
            val result = rpcCall { service.save(current.loaded.versionedId, current.draft) }
            current.saving = false
            if (editor !== current) {
                return@launch
            }
            when (result) {
                is AdminResult.Success -> editor = ClubEditorState(result.value)
                is AdminResult.Failure -> current.showFailure(result.error)
            }
        }
    }

    fun reloadAfterConflict() {
        val current = editor ?: return
        if (current.reloading) {
            return
        }
        current.reloading = true
        scope.launch {
            try {
                load(replacing = current)
            } finally {
                current.reloading = false
            }
        }
    }

    AdminPageHeader(title = "Verein", beta = true, unsavedChanges = editable && state?.hasChanges == true) {
        // no "Auf Website ansehen" until the public site has /verein (#113)
        if (editable && state != null) {
            AdminButton(disabled = state.busy || !state.hasChanges, onClick = { editor = ClubEditorState(state.loaded) }) {
                Text("Verwerfen")
            }
            AdminButton(
                variant = ButtonVariant.PRIMARY,
                type = ButtonType.Submit,
                disabled = state.busy || !state.hasChanges,
                form = FORM_ID,
            ) { Text(if (state.saving) "Speichert …" else "Speichern") }
        }
    }

    AdminPageBody {
        when {
            state != null -> Form(attrs = {
                id(FORM_ID)
                // one form, so it stays at the form's width (design system v27)
                classes(stack.block, stack.modifier("form"))
                attr("novalidate", "")
                onSubmit { event ->
                    event.preventDefault()
                    save()
                }
            }) {
                if (state.conflict) {
                    AdminConflictCallout(onReload = ::reloadAfterConflict)
                }
                state.error?.let {
                    P({
                        classes(alert.block)
                        attr("role", "alert")
                    }) { Text(it) }
                }

                AdminCard(title = "Verein", id = "verein") {
                    AdminCallout {
                        Text("Die Website zeigt diese Texte auf der Seite ")
                        Strong { Text("„Verein“") }
                        Text("; die Kurzbeschreibung steht auch auf der Startseite.")
                    }

                    if (editable) {
                        // nothing changes while saving or reloading, so the answer can replace the editor without dropping input
                        Fieldset({
                            classes(fieldset.block)
                            if (state.busy) {
                                attr("disabled", "")
                            }
                        }) {
                            ClubFields(state)
                        }
                    } else {
                        Span({ classes(card.element("hint").className) }) { Text("Nur Superuser können die Vereinstexte ändern.") }
                        AdminTextView(id = "verein-kurz", label = "Kurzbeschreibung", value = state.loaded.draft.shortDescription)
                        AdminMarkdownView(id = "verein-text", label = "Text", value = state.loaded.draft.text)
                        AdminMarkdownView(id = "verein-mitmachen", label = "Mitmachen", value = state.loaded.draft.joinText)
                    }

                    Span({ classes(card.element("hint").className) }) { Text(contactPersonHint(state.loaded.contactPerson)) }
                    Span({ classes(card.element("hint").className) }) {
                        Text("Die Kontakt-E-Mail-Adresse für die Website wird im Impressum gepflegt. ")
                        RouteLink(Route.Imprint, onNavigate, button.block, button.modifier("link")) { Text("Zum Impressum") }
                    }
                    Span({ classes(card.element("hint").className) }) { Text(lastChanged(state.loaded)) }
                }
            }

            loadError != null -> P({
                classes(alert.block)
                attr("role", "alert")
            }) { Text(loadError!!) }

            else -> P { Text("Lade …") }
        }
    }
}

@Composable
private fun ClubFields(state: ClubEditorState) {
    val draft = state.draft
    AdminTextAreaField(
        id = "verein-kurz",
        label = "Kurzbeschreibung",
        value = draft.shortDescription,
        onValueChange = { value -> state.edit(ClubDraft.SHORT_DESCRIPTION) { copy(shortDescription = value) } },
        rows = 2,
        error = state.errorOf(ClubDraft.SHORT_DESCRIPTION),
    )
    AdminMarkdownField(
        id = "verein-text",
        label = "Text",
        value = draft.text,
        onValueChange = { value -> state.edit(ClubDraft.TEXT) { copy(text = value) } },
        error = state.errorOf(ClubDraft.TEXT),
        rows = 14,
    )
    AdminMarkdownField(
        id = "verein-mitmachen",
        label = "Mitmachen",
        value = draft.joinText,
        onValueChange = { value -> state.edit(ClubDraft.JOIN_TEXT) { copy(joinText = value) } },
        error = state.errorOf(ClubDraft.JOIN_TEXT),
        hint = "Markdown wird unterstützt. Steht als Hinweis auf der Vereinsseite und der Startseite.",
        rows = 4,
    )
}

/** Who the website names as the contact: the board member with the function "Obmann", or nobody. */
internal fun contactPersonHint(contactPerson: String?): String = if (contactPerson != null) {
    "Als Ansprechperson zeigt die Website, wer im Vorstand die Funktion „${Club.CONTACT_FUNCTION}“ hat: $contactPerson."
} else {
    "Im Vorstand hat niemand die Funktion „${Club.CONTACT_FUNCTION}“; die Website zeigt dann keine Ansprechperson."
}

/** "Zuletzt geändert am 28.10.2026 von …", without the name for the migration's texts or a deleted account. */
internal fun lastChanged(club: Club): String =
    "Zuletzt geändert am ${formatDate(club.changedAt)}" + club.changedBy?.let { " von $it" }.orEmpty()

private const val FORM_ID = "verein"
